/**
 * The query's source graph: which entities a widget reads, and how they join
 * (CT-5183, multi-entity authoring).
 *
 * A composite is several tables. Until now a widget could only reference the
 * primary's fields — anything else was
 * `DSW018 Fields [...] are not fields of the chosen entity` on save. What
 * authorises a field from another table is **declaring where it comes from**:
 *
 * ```jsonc
 * "sources": [
 *   { "key": "s1", "entityUuid": "<primary>", "role": "ROOT" },
 *   { "key": "s2", "entityUuid": "<other>",   "role": "RELATED",
 *     "join": { "left": "s1", "route": "MEMBER",
 *               "memberUuid": "<member>", "joinType": "LEFT" } }
 * ]
 * ```
 *
 * and then tagging each field that is not the root's with its `sourceKey`.
 *
 * ## Derived, never rehydrated
 *
 * `GET /v0/dashboard/widget/{uuid}` answers `sources: []` and `sourceKey:
 * null` on every column, even for a widget saved with a graph — the read
 * mapper has not caught up (backend, 2026-10-02). That would normally make a
 * saved multi-entity widget impossible to edit without silently dropping its
 * related sources on the next save.
 *
 * It does not here, because **nothing is read back**: the graph is recomputed
 * from the fields themselves. Every column carries its origin, which comes
 * from the composite's field map (`composite-entity/field-map/list`), so
 * reopening a widget and re-deriving its graph gives the same answer as
 * creating it. The backend's gap costs nothing as long as this stays derived.
 *
 * ## Both join routes
 *
 * `MEMBER` for another table of the same composite, keyed by `memberUuid`.
 * `COMPOSITE_RELATIONSHIP` for a hop to a **different** composite, keyed by
 * the relationship and the direction to travel it.
 *
 * Which one a field takes is read off its origin, never asked: a field picked
 * from a neighbouring composite carries that composite's relationship, and a
 * field from a member carries its member uuid. The author picks a field; the
 * route follows.
 */

/** The root's own key. Everything else is numbered after it. */
const ROOT_KEY = 's1';

/**
 * LEFT, not INNER, on both routes: a record with nothing on the other side is
 * still a record of this composite, and an inner join would drop it.
 */
const JOIN_TYPE = 'LEFT';

/** A hop to another composite: the relationship and the way to travel it. */
const relationshipJoin = (origin) => (
  origin.relationshipUuid && origin.direction
    ? {
      left: ROOT_KEY,
      route: 'COMPOSITE_RELATIONSHIP',
      compositeRelationshipUuid: origin.relationshipUuid,
      direction: origin.direction,
      joinType: JOIN_TYPE,
    }
    : null
);

/** Another table of the same composite. */
const memberJoin = (origin) => (
  origin.memberUuid
    ? {
      left: ROOT_KEY, route: 'MEMBER', memberUuid: origin.memberUuid, joinType: JOIN_TYPE,
    }
    : null
);

/**
 * A field is the root's when it says so, or when it says nothing: a column
 * with no origin predates the field map, and the only entity it can belong to
 * is the one the Data step was reading.
 */
const isRootField = (origin, rootEntityUuid) => (
  !origin?.entityUuid || origin.entityUuid === rootEntityUuid
);

/**
 * Build the graph for a set of fields.
 *
 * @param {object} params
 * @param {string} params.rootEntityUuid The composite's primary member — what
 *   the Data step carries as `entityRef`.
 * @param {Array<object|null|undefined>} params.origins One per referenced
 *   field, in any order; `null` for a field whose origin is unknown.
 * @returns {{ sources: Array|null, keyByEntityUuid: Map<string,string> }}
 *   `sources` is `null` when every field is the root's — a single-entity
 *   widget sends no graph at all, exactly the payload it sent before this
 *   existed.
 */
export function buildQuerySources({ rootEntityUuid, origins = [] }) {
  if (!rootEntityUuid) return { sources: null, keyByEntityUuid: new Map() };

  // One source per related entity, in the order the fields first mention it,
  // so the same widget always produces the same keys.
  const related = [];
  const seen = new Map();
  origins.forEach((origin) => {
    if (isRootField(origin, rootEntityUuid)) return;
    if (seen.has(origin.entityUuid)) return;

    /*
     * The route the field's own origin implies. A hop to another composite
     * wins when it is there: a neighbour's field is reached through the
     * relationship even though it also sits in some member table of its own,
     * and that member means nothing to this composite.
     *
     * Either way a source needs a join, and a source with no join is refused
     * — so a field whose origin names neither is skipped rather than emitted
     * half-formed. It will fail on its own, which is a clearer failure than
     * an invalid graph taking the whole payload down.
     */
    const join = relationshipJoin(origin) ?? memberJoin(origin);
    if (!join) return;

    const key = `s${related.length + 2}`;
    seen.set(origin.entityUuid, key);
    related.push({
      key, entityUuid: origin.entityUuid, role: 'RELATED', join,
    });
  });

  if (!related.length) return { sources: null, keyByEntityUuid: new Map() };

  return {
    sources: [
      { key: ROOT_KEY, entityUuid: rootEntityUuid, role: 'ROOT' },
      ...related,
    ],
    keyByEntityUuid: seen,
  };
}

/**
 * The `sourceKey` a field carries, or `undefined` for the root's own.
 *
 * `undefined` rather than `null`: the key is dropped when the body is
 * serialised, which is what the contract asks for — the root's fields go
 * without one.
 */
export const sourceKeyFor = (origin, keyByEntityUuid) => (
  origin?.entityUuid ? keyByEntityUuid.get(origin.entityUuid) : undefined
);

export default buildQuerySources;
